import { Injectable } from '@angular/core';
import {
  PanierReservation,
  PresenceState, PresenceStatus, Reservation,
  ReservationError, ReservationPanierItem, ReservationPresence, ReservationStatus
} from '@app/models/reservation';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { FamilyService } from './family.service';
import { Inscription } from '@app/models/inscription';
import { Family } from '@app/models/family';
import { Facture } from '@app/models/facturation';
import { HTTP_OPTIONS_JSON } from './api-crud.service';
import { ApiPlanningData, DatesLoaded, PlanningData } from '@app/components/_user/reservation/planning/planning-data';
import { Consumer } from '@app/models/consumer';
import moment from 'moment';
import { DATE_FORMAT, PlanningService } from './planning.service';
import { CustomHttpService, WaitingRoomService } from '@app/services';
import { clone } from '@app/utils/object';

interface PanierFactureData {
  facture?: Facture;
  montantAvoir?: number;
  errors?: ReservationError[];
}

export interface SubmitResult {
  creation?: boolean;
  validation?: boolean;
  errors?: ReservationError[];
  traces?: string[];
  facture?: any;
  messages?: string[];

  reservation: Reservation;
  attentes?: ReservationError[];
}

export function computePresenceStatus(pr: ReservationPresence, reservation?: Reservation): PresenceStatus {
  if (reservation?.state === 'Expiree') {
    return pr.askCancel ? 'expired_canceling' : 'expired';
  }

  if (pr.askCancel) {
    return 'canceling';
  }

  if (!pr.id) {
    return 'new';
  }

  switch (pr.state) {
    case PresenceState.waiting: return 'waiting';
    case PresenceState.canceled: return 'canceled';
    case PresenceState.denied: return 'denied';
    case PresenceState.accepted: return 'accepted';
    case PresenceState.attente: return 'liste_attente';
  }
}

export function translateReservationStatus(resa: Reservation): ReservationStatus {
  if (resa.otherAccount) {
    return 'other_account';
  }

  switch (resa.state) {
    case 'EnAttente': return 'waiting';
    case 'EnAttenteDePaiement': return 'waiting_payment';
    case 'Acceptee': return 'accepted';
    case 'Refusee': return 'denied';
    case 'Annulee': return 'canceled';
    case 'Expiree': return 'expired';
    case 'PartiellementAccepte': return 'partially_accepted';
  }
}

@Injectable({
  providedIn: 'root'
})
export class ReservationService {

  private cache_planningDataByFamily = new Map<number, PlanningData>();
  get planningData(): PlanningData {
    const idFamily = this.familyService.currentFamily.id
    if (!this.cache_planningDataByFamily.has(idFamily)) {
      this.cache_planningDataByFamily.set(idFamily, new PlanningData())
    }
    return this.cache_planningDataByFamily.get(idFamily)
  }

  constructor(
    private http: CustomHttpService,
    private familyService: FamilyService,
    private waitingRoomService: WaitingRoomService,
    private planningService: PlanningService
  ) { }

  // Plus utile, était appelé seulement pour les réservations / plannings mais désormais toutes les données sont renvoyées par l'appel "planning-data"
  // getFamilyInscriptions(family: Family | number) {
  //   const id = typeof family === 'object' ? family.id : family;
  //   this.waitingRoomService.waitingForRequest$.next(true)
  //   return this.http.get<Inscription[]>(`familles/${id}/inscriptions`)
  //     .pipe(
  //       tap(_ => this.waitingRoomService.waitingForRequest$.next(false))
  //     );
  // }

  submitReservation(resa: Reservation, forSummary = false): Observable<SubmitResult> {
    let presences = [].concat(resa.presences.flatMap(pr => this.adaptPresenceWithAttente(pr)))

    // If clonedFromForListAttente belong to a group of presences, we must change 'group' as it shouldn't be the same group as original presence
    let groupsMap = {}
    presences.filter(p => !!p.clonedFromForListAttente && !!p.group).forEach(p => {
      if (!groupsMap[p.group]) {
        groupsMap[p.group] = this.planningService.generateId()
      }
      p.group = groupsMap[p.group]
    })

    const data = {
      idPeriode: resa.idPeriode,
      idPortailPeriode: resa.idPortailPeriode,
      idInscription: resa.idInscription,
      presences
    };

    const url = 'reservations' + (forSummary ? '/prevalidate' : '');

    this.waitingRoomService.waitingForRequest$.next(true)
    return this.http.post<SubmitResult>(url, data, HTTP_OPTIONS_JSON)
      .pipe(
        tap(_ => this.waitingRoomService.waitingForRequest$.next(false))
      );
  }

  getReservationCart(): Observable<ReservationPanierItem[]> {

    if (this.familyService.currentFamily) {
      this.waitingRoomService.waitingForRequest$.next(true)
      return this.http.get<ReservationPanierItem[]>(`familles/${this.familyService.currentFamily.id}/panier`).pipe(
        tap(_ => this.waitingRoomService.waitingForRequest$.next(false)),
        map(data => {
          data = data || [];

          data.forEach(item => {
            item.reservations = item.reservations.map(r => this.adaptReservation(r));
          });

          return data;
        })
      );
    }

    return of(null);
  }

  getFactureForReservations(idRegie: number, idReservations: number[]) {
    const idFamily = this.familyService.currentFamily.id;

    this.waitingRoomService.waitingForRequest$.next(true)
    return this.http.post<PanierFactureData>(`familles/${idFamily}/panier/${idRegie}/facture`, { reservations: idReservations })
      .pipe(
        tap(_ => this.waitingRoomService.waitingForRequest$.next(false))
      );
  }

  validatePanier(idRegie: number, reservations: number[], montantPaiement: number, backUrl: string) {
    const idFamily = this.familyService.currentFamily.id;

    this.waitingRoomService.waitingForRequest$.next(true)
    return this.http.post<any>(`familles/${idFamily}/panier/${idRegie}/valider`, { reservations, montantPaiement, backUrl })
      .pipe(
        tap(_ => this.waitingRoomService.waitingForRequest$.next(false))
      );
  }

  loadPlanningData(family: Family | number, dates: { start: string, end: string }, loadUsagers: boolean, forceRefresh: boolean): Observable<ApiPlanningData> {
    const idFamily = typeof family === 'object' ? family.id : family;

    let url = `reservations/planning-data?family=` + idFamily;

    if (dates) {
      // load explicit dates
      url += '&startDate=' + dates.start + '&endDate=' + dates.end;
    }

    // exclude already loaded
    if (this.planningData.loadedDates$.value?.length) {
      url += '&loaded_dates=' + JSON.stringify(this.planningData.loadedDates$.value);
    }

    // tell which periodes & reservations we already know
    const loadedReservations = this.planningData.reservations?.filter(r => r.id !== 'new')
    if (loadedReservations.length) {
      url += '&loaded_reservations=' + loadedReservations.filter(r => r.id !== 'new').map(r => r.id).join(',');
    }


    if (this.planningData.activityDetails?.length) {
      url += '&loaded_activities=' + this.planningData.activityDetails?.map(a => a.id + '-' + a.periode).join(',');
    }

    if (!this.planningData.feries?.length) {
      url += '&load_feries=1';
    }

    if (loadUsagers) {
      url += '&load_usagers=1';
    }

    const req = forceRefresh ? this.http.get<ApiPlanningData>(url) : this.http.getWithCache<ApiPlanningData>(url, 0.5)
    // ^^ cache de 30 secondes (1/2 de minutes) uniquement car ces données doivent se raffraichir vite
    // ( de toutes façons, pas sûr que ce soit utile de passer par getWithCache ici parcequ'avec les
    // paramètres loaded_dates | loaded_reservations | etc ... les urls ne seront pas souvent les mêmes
    // mais ça peut servir dans certains cas (pas encore de résa , ou que des résa 'fromDomino') )

    return req.pipe(
      tap(resp => {
        if (resp.reservations) {
          resp.reservations = resp.reservations.map(item => this.adaptReservation(item));
        }

        let usagersUpdated = forceRefresh || false// if forceRefresh, mean we must emit usagers too even if they not changed (to trigger refreshDisplayFilters in planning Component)
        if (resp.usagers && resp.inscriptions) {
          usagersUpdated = this.planningData.setUsagers(resp.usagers, resp.inscriptions) || loadUsagers
        }
        this.planningData.addPlanningData(resp);
        this.planningData.setLoadedDates(dates && !forceRefresh ? dates : 'all');

        this.planningData.triggerAllChanges(usagersUpdated);
      })
    );
  }

  private _planningDataLoadingProgressive$: Observable<ApiPlanningData>;
  private _firstMonthLoaded = false
  loadPlanningDataProgressive(family: Family | number): Observable<ApiPlanningData> {
    const lastFullLoad = this.planningData.allDatesLoaded$.value
    const cacheTimeout = 1000 * 60 * 5 // 5 minutes cache to avoid too frequent calls to the API

    if (lastFullLoad && (new Date()).getTime() - lastFullLoad.getTime() < cacheTimeout) {
      // console.warn('use PlanningData in cache ? / this.planningData.datesToReload?.length', this.planningData.datesToReload?.length)

      if (this.planningData.datesToReload?.length > 0) { // We have some dates to reload

        const datesToReload = this.planningData.datesToReload.reduce((acc, cur) => {
          const start = acc.start !== '' && acc.start < cur.start ? acc.start : cur.start
          const end = acc.end !== '' && acc.end > cur.end ? acc.end : cur.end
          return { start, end }
        }, { start: '', end: '' })

        return this.refreshPartialPlanningData(family, datesToReload.start, datesToReload.end)

      } else {
        // console.warn('use PlanningData in cache => go')
        //  Les données actuelles sont OK => On emet tout de suite
        this.planningData.triggerAllChanges(true)
        return of(null)
      }
    } else {

      // Since the 'cache' mechanism above is not enough because it does not work when the user passes
      // from the ReservationsComponent to the UserReservationEditComponent before the end of the full loading
      // => we use the same technique that in CustomHttpService.getWithCache to share this observable.
      // So this._planningDataLoading$ is the observable that can be shared for max 5 seconds after it complete
      if (this._planningDataLoadingProgressive$) {
        // we first take these observables, because if we are here, it mean that this._planningDataLoadingProgressive$
        // is running, and perhaps onChangeUsager$ has already emit via triggerAllChanges for the first month,
        // so the component who claim the data will not receive the onChangeUsager$ since its a simple subject.

        if (this._firstMonthLoaded) {
          this.planningData.triggerAllChanges(true)
        }
        return this._planningDataLoadingProgressive$

      } else {
        // console.warn('request for PlanningData from API ', lastFullLoad, lastFullLoad ? lastFullLoad.getTime() : '', cacheTimeout)
        this.planningData.loadedDates$.next([])
        this.planningData.allDatesLoaded$.next(null)
        this._firstMonthLoaded = false;

        const firstLoad: DatesLoaded = this.planningData.getCurrentMonthBounds();
        const secondLoad: DatesLoaded = {
          start: moment(firstLoad.start).subtract(3, 'month').format(DATE_FORMAT),
          end: moment(firstLoad.end).add(3, 'month').format(DATE_FORMAT)
        };

        const switchToNextPlanningDataLoad = (conf?: DatesLoaded) => {
          if (!this.planningData.consumers?.length) {
            return of(null)
          } else {
            return this.loadPlanningData(family, conf, false, false)
          }
        }

        const timeoutInMs = 5 // mean 5ms after full loading, and after this, the cache will take over
        this.waitingRoomService.waitingForRequest$.next(true)

        this._planningDataLoadingProgressive$ = this.loadPlanningData(family, firstLoad, true, false).pipe(
          tap(_ => {
            this._firstMonthLoaded = true;
            this.waitingRoomService.waitingForRequest$.next(false)
          }),
          // takeUntil(cancel$),
          switchMap(_ => switchToNextPlanningDataLoad(secondLoad)),
          switchMap(_ => switchToNextPlanningDataLoad()),
          finalize(() => {
            setTimeout(() => {
              this._planningDataLoadingProgressive$ = null
            }, timeoutInMs)
          }),
          shareReplay({ bufferSize: 1, windowTime: 0, refCount: false })

        );
        return this._planningDataLoadingProgressive$
      }
    }
  }

  addDatesToReload(startDate, endDate): void {
    this.planningData.datesToReload.push({ start: startDate, end: endDate })
  }

  refreshPartialPlanningData(family: Family | number, startDate, endDate): Observable<ApiPlanningData> {
    // console.log('refreshPartialPlanningData / family: ', family, 'startDate:', startDate, 'endDate', endDate)
    const reloadConf = {
      start: moment(startDate).format(DATE_FORMAT),
      end: moment(endDate).format(DATE_FORMAT)
    };
    this.planningData.loadedDates$.next([])
    this.planningData.allDatesLoaded$.next(null)

    // Remove presences between startDate and endDate, because they should be reloaded
    this.planningData.reservations.forEach(resa => {
      resa.presences = resa.presences.filter(p => p.date < startDate || p.date > endDate)
    })

    return this.loadPlanningData(family, reloadConf, false, true).pipe(
      tap(_ => {
        // Remove this refresh from datesToReload
        this.planningData.datesToReload = this.planningData.datesToReload.filter(dates => dates.start < startDate || dates.end > endDate)
      }))
  }

  adaptReservation(reservation: Reservation) {
    reservation.status = translateReservationStatus(reservation);

    if (reservation.presences) {
      for (const prez of reservation.presences) {
        if (!reservation.startDate || prez.date < reservation.startDate) { reservation.startDate = prez.date; }
        if (!reservation.endDate || prez.date > reservation.endDate) { reservation.endDate = prez.date; }
        prez.status = computePresenceStatus(prez, reservation);
      }
    }

    reservation.idConsumer = Consumer.buildIdConsumer(reservation.idChild, reservation.idAdult);
    reservation.number = typeof reservation.id === 'number' ? ('' + reservation.id).padStart(6, '0') : reservation.id;

    return reservation;
  }

  cancelReservationFromCart(reservation: PanierReservation) {
    return this.http.put(`reservations/${reservation.id}/cancel`, {});
  }

  // Pour l'enrgistrement des réservations avec liste d'attente (et activités)
  // Permet de déterminer si la présence doit être envoyée ou pas selon ce que l'utilisateur a pu choisir
  // Nécessaire depuis que l'utilisateur peut choisir d'enregistrer une présence seule si l'activité demandée n'est pas disponible mais que la présence seule oui ('addSinglePres').
  isPresenceSelected(presence: ReservationPresence) {
    return this.adaptPresenceWithAttente(presence).length > 0
  }

  adaptPresenceWithAttente(presence: ReservationPresence): ReservationPresence[] {

    if (presence.attente === null) return [presence]; // pas d'attente du tout => On renvoie direct la présence

    // Le but ici est de vérifier si on doit cloner ou pas la présence pour les cas où une activité est complète, mais :
    // **CAS N°1** qu'il reste de la place pour la présnece seule et en prenant compte des choix de l'utilisateur ...
    //    - Pres1 => ira sur liste d'attente on conserve uniquement les activités qui sont "full" (attente===1)
    //    - Pres2 => a pour vocation de n'enregistrer que ce qui peut être validé  (attente !==-1 , car peut être null si une activité n'est pas complète)
    // -----------------------------------
    // **CAS N°2** qu'il ne reste pas de place pour la présence seule (donc présence seule en liste d'attente aussi)
    // Ici, si la présence n'est pas réservable non plus, il faut quand même laisser le choix à l'usager de pouvoir placer la présence seule sur liste d'attente.
    // Dans ce cas, on aura donc 2 presences en liste d'attente et on place toutes les activités dans la même présence qui ira sur liste d'attente
    //   - Pres1 =>  vers liste d'attente, avec toutes les activités (complètes où non / selon choix utilisateur)
    //   - Pres2 =>  vers liste d'attente,  sans aucune activité

    // console.log('adaptPresenceWithAttente', presence)

    if (presence.attenteDetail?.canDuplicateWithoutActivity) {
      let result: ReservationPresence[] = []

      const canValidatePresence = !(presence.attenteDetail?.fullPlages?.length > 0)

      if (canValidatePresence) { //
        const pres1: ReservationPresence = clone(presence) // => Pour aller sur liste d'attente
        // On retire les activités pour lesquelles l'utilisateur à décocher "inscrire sur liste d'attente"
        pres1.activities = presence.activities?.filter(act => act.attente === 1) || []
        pres1.tmpId = this.planningService.generateId() // attribution d'un nouvel ID pour bien distinguer les 2 présenses
        pres1.clonedFromForListAttente = presence.tmpId

        // Et on ajoute la presence qui ira sur liste d'attente (pour les activités qui sont complètes)
        if (pres1.activities.length > 0) {
          result.push(pres1)
        }
        if (presence.attenteDetail?.canDuplicateWithoutActivity && presence.attenteDetail?.addSinglePres !== -1) {
          // Ici on enregistre la demande "acceptable SANS liste d'attente" (donc on retire les activités qui n'ont plus de place)
          const pres2: ReservationPresence = clone(presence)
          pres2.activities = presence.activities?.filter(act => !presence.attenteDetail?.fullActivities.includes(act.id)) || []
          result.push(pres2)
        }

      } else {

        const pres1: ReservationPresence = clone(presence) // => Pour aller sur liste d'attente
        // On retire les activités pour lesquelles l'utilisateur à décocher "inscrire sur liste d'attente" mais on garde les activités qui ne sont pas complètes (attente=null)
        pres1.activities = presence.activities?.filter(act => act.attente !== -1) || []
        pres1.tmpId = this.planningService.generateId() // attribution d'un nouvel ID pour bien distinguer les 2 présenses
        pres1.clonedFromForListAttente = presence.tmpId

        // Et on ajoute la presence qui ira sur liste d'attente (pour les activités qui sont complètes)
        if (pres1.activities.length > 0) {
          result.push(pres1)
        }
        if (presence.attenteDetail?.canDuplicateWithoutActivity && presence.attenteDetail?.addSinglePres !== -1) {
          // Ici on enregistre la demande sans activités
          const pres2: ReservationPresence = clone(presence)
          pres2.activities = []
          result.push(pres2)
        }
      }

      return result
    } else { // canDuplicateWithoutActivity est faux, donc cela signifie qu'on ne peut rien dissocier ...
      if (presence.attente !== -1) return [presence]
    }
    return []

  }

}
